// Queues screen — Phase 2a of the revised Pamosi Clinic EMR framework.
//
// Lists open visit_queue entries (waiting + in_progress) with a department
// filter and per-row actions:
//   - Pick up    → creates an encounter, flips queue entry to in_progress
//                  (routes clinician to the encounter detail screen).
//   - Done       → closes the entry (status='done'); used by pharmacist or
//                  ancillary dept when there is no encounter to finalize.
//   - Cancel     → no-show or wrong patient (status='cancelled').
//   - Reroute    → clinician picked up the wrong patient; reopen the dept
//                  picker so reception can send them elsewhere.
//
// Access: receptionist + all clinical roles (see permissions.ts). Reception
// uses this to manage the day's board; clinicians pick up patients from
// their own department.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ClinicDepartment, VisitQueueEntryView } from '@pamosi/shared';
import { CLINIC_DEPARTMENT_KIND_LABELS, PATIENT_CARD_TYPE_LABELS } from '@pamosi/shared';
import { ApiError, encountersApi, queuesApi, receptionApi } from '../lib/api';
import { AdmissionsBoard } from '../components/AdmissionsBoard';

type Segment = 'departments' | 'admissions';

export function QueuesScreen() {
    const [segment, setSegment] = useState<Segment>('departments');
    const [departments, setDepartments] = useState<ClinicDepartment[]>([]);
    const [deptId, setDeptId] = useState<string>('');
    const [deptLoadError, setDeptLoadError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await receptionApi.listDepartments();
                if (cancelled) return;
                setDepartments(res.departments);
            } catch (err) {
                if (cancelled) return;
                setDeptLoadError(
                    err instanceof ApiError ? err.message || err.code : 'Could not load departments.',
                );
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Queues</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {segment === 'departments'
                            ? 'Patients waiting at departments. Oldest first; priority rows surface above.'
                            : 'Patients currently in observation. Overdue vitals rise to the top.'}
                    </p>
                </div>
            </div>

            {/* Segmented control — Phase 3. Admissions tab is a sibling of
                Departments inside Queues per the 8-tab nav cap. */}
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                <button
                    type="button"
                    className={
                        'px-3 py-1.5 text-sm font-medium rounded ' +
                        (segment === 'departments'
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:text-slate-900')
                    }
                    onClick={() => setSegment('departments')}
                >
                    Departments
                </button>
                <button
                    type="button"
                    className={
                        'px-3 py-1.5 text-sm font-medium rounded ' +
                        (segment === 'admissions'
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:text-slate-900')
                    }
                    onClick={() => setSegment('admissions')}
                >
                    Admissions
                </button>
            </div>

            {segment === 'admissions' ? (
                <AdmissionsBoard />
            ) : (
                <DepartmentsSection
                    departments={departments}
                    deptId={deptId}
                    setDeptId={setDeptId}
                    deptLoadError={deptLoadError}
                />
            )}
        </div>
    );
}

function DepartmentsSection({
    departments,
    deptId,
    setDeptId,
    deptLoadError,
}: {
    departments: ClinicDepartment[];
    deptId: string;
    setDeptId: (v: string) => void;
    deptLoadError: string | null;
}) {
    const queueQ = useQuery({
        queryKey: ['queues', deptId || 'all'],
        queryFn: () => queuesApi.list(deptId ? { departmentId: deptId } : {}),
        refetchInterval: 15_000,
    });
    return (
        <>
            <div className="pm-card p-3">
                <label className="text-slate-600 text-xs block mb-1">Department</label>
                <select
                    className="pm-input"
                    value={deptId}
                    onChange={(e) => setDeptId(e.target.value)}
                    disabled={departments.length === 0}
                >
                    <option value="">All departments</option>
                    {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                            {d.name} · {CLINIC_DEPARTMENT_KIND_LABELS[d.kind]}
                        </option>
                    ))}
                </select>
                {deptLoadError && <div className="text-rose-700 text-xs mt-2">{deptLoadError}</div>}
            </div>

            {queueQ.isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>
            )}
            {queueQ.isError && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">Can’t load the queue</div>
                    <div className="text-slate-500 text-sm mt-1">Check your connection and try again.</div>
                </div>
            )}
            {queueQ.data && queueQ.data.entries.length === 0 && (
                <div className="pm-card p-8 text-center">
                    <div className="text-slate-800 font-medium">Nothing waiting</div>
                    <div className="text-slate-500 text-sm mt-1">
                        New entries appear here when reception sends a patient to a department.
                    </div>
                </div>
            )}

            {queueQ.data && queueQ.data.entries.length > 0 && (
                <div className="space-y-2">
                    {queueQ.data.entries.map((e) => (
                        <QueueRow key={e.id} entry={e} />
                    ))}
                </div>
            )}
        </>
    );
}

function QueueRow({ entry }: { entry: VisitQueueEntryView }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['queues'] });
    };

    const pickUp = useMutation({
        mutationFn: async () => {
            // If the queue entry already carries an encounter (triage
            // completion threads it forward via enqueueVisit), reuse it —
            // creating a new encounter here would orphan the triage data
            // and leave two parallel encounters for the same visit.
            // Fresh reception walk-ins have no encounter yet, so we
            // create one in that case only.
            let encounterId = entry.encounter_id;
            if (!encounterId) {
                const enc = await encountersApi.create({
                    patientId: entry.patient_id,
                    departmentId:entry.department_id,
                    encounterType: 'general',
                    ...(entry.chief_complaint ? { chiefComplaint: entry.chief_complaint } : {}),
                    bypassConsent: true,
                });
                encounterId = enc.encounter.id;
            }
            await queuesApi.updateStatus(entry.id, {
                status: 'in_progress',
                encounterId,
            });
            return encounterId;
        },
        onSuccess: (encounterId) => {
            invalidate();
            navigate(`/encounters/${encounterId}`);
        },
        onError: (e) => setError(prettyError(e)),
    });

    const close = useMutation({
        mutationFn: () => queuesApi.updateStatus(entry.id, { status: 'done' }),
        onSuccess: () => invalidate(),
        onError: (e) => setError(prettyError(e)),
    });

    const cancel = useMutation({
        mutationFn: () => queuesApi.updateStatus(entry.id, { status: 'cancelled' }),
        onSuccess: () => invalidate(),
        onError: (e) => setError(prettyError(e)),
    });

    const reroute = useMutation({
        mutationFn: () => queuesApi.updateStatus(entry.id, { status: 'rerouted' }),
        onSuccess: () => invalidate(),
        onError: (e) => setError(prettyError(e)),
    });

    const busy = pickUp.isPending || close.isPending || cancel.isPending || reroute.isPending;

    const waited = useMemo(() => formatWaited(entry.enqueued_at), [entry.enqueued_at]);
    const patientLabel = entry.patient_full_name?.trim() || entry.patient_phone_e164 || 'Unknown patient';

    return (
        <div className="pm-card p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-slate-900 font-medium">{patientLabel}</div>
                        {entry.card_type && (
                            <span className="text-xs uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                                {PATIENT_CARD_TYPE_LABELS[entry.card_type]}
                            </span>
                        )}
                        {entry.hospital_number && (
                            <span className="text-slate-500 text-xs">#{entry.hospital_number}</span>
                        )}
                        <span
                            className={
                                'text-xs uppercase tracking-wide px-2 py-0.5 rounded '
                                + (entry.status === 'in_progress'
                                    ? 'bg-brand-50 text-brand-700'
                                    : 'bg-amber-50 text-amber-700')
                            }
                        >
                            {entry.status === 'in_progress' ? 'In progress' : 'Waiting'}
                        </span>
                    </div>
                    <div className="text-slate-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
                        <span>{entry.department_name}</span>
                        <span aria-hidden>·</span>
                        <span>{entry.patient_phone_e164}</span>
                        <span aria-hidden>·</span>
                        <span>waiting {waited}</span>
                    </div>
                    {entry.chief_complaint && (
                        <div className="text-slate-600 text-sm mt-1">{entry.chief_complaint}</div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {entry.status === 'in_progress' && entry.encounter_id && (
                        <button
                            type="button"
                            className="pm-btn-outline text-xs"
                            onClick={() => navigate(`/encounters/${entry.encounter_id}`)}
                        >
                            Open encounter
                        </button>
                    )}
                    {entry.status === 'waiting' && (
                        <button
                            type="button"
                            className="pm-btn-primary text-xs"
                            disabled={busy}
                            onClick={() => pickUp.mutate()}
                        >
                            {pickUp.isPending ? 'Picking up…' : 'Pick up'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="pm-btn-outline text-xs"
                        disabled={busy}
                        onClick={() => close.mutate()}
                    >
                        {close.isPending ? 'Closing…' : 'Done'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => {
                            if (window.confirm(`Reroute ${patientLabel}? Reception will need to send them again.`)) {
                                reroute.mutate();
                            }
                        }}
                    >
                        Reroute
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs text-rose-700"
                        disabled={busy}
                        onClick={() => {
                            if (window.confirm(`Cancel this queue entry for ${patientLabel}?`)) {
                                cancel.mutate();
                            }
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
            {error && <div className="text-rose-700 text-xs mt-2">{error}</div>}
        </div>
    );
}

function formatWaited(enqueuedAt: string): string {
    const t = new Date(enqueuedAt).getTime();
    if (!Number.isFinite(t)) return 'recently';
    const diffMin = Math.max(0, Math.round((Date.now() - t) / 60_000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d`;
}

function prettyError(e: unknown): string {
    if (!(e instanceof ApiError)) return 'Something went wrong.';
    switch (e.code) {
        case 'not_found':
            return 'This queue entry no longer exists.';
        case 'insufficient_role':
            return 'Your role can’t update this queue.';
        case 'encounter_id_required':
            return 'An encounter is required to pick up.';
        default:
            return 'Update failed. Try again.';
    }
}
